// The new-project handoff contract, in one place.
//
// Two surfaces create projects — the dashboard composer (free-form description,
// blank canvas, optional file attachments) and `/learn` (a template + tutorial
// launcher) — and both hand `ProjectPage` the same `location.state` payload,
// which its two ref-guarded one-shot effects feed straight to the EXISTING
// `handleUnitSystemConfirm` / `handleProblemStatementConfirm`. Neither caller
// reimplements project bootstrapping; this module is the shape they must agree
// on, and `buildProjectHandoff` is where the normalisation lives so it can be
// tested (ProjectPage cannot be rendered under jest — ReactFlow/Three/Plotly).
//
// Attachment classification and the brief lines live in
// `constants/attachments.js` instead, so the composer and its chip component can
// use them without importing react-router through this file.

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject, summarizeProject } from '../api/projects';

/**
 * The `navigate` options object for a freshly created project.
 *
 * `isNew` is what makes `ProblemStatementModal` the fallback for a project
 * created any other way; `newProject` is what suppresses it here. All four of
 * `context`/`templateKey`/`unitSystemId`/`tutorial`/`autoMode` are always
 * present because `handleProblemStatementConfirm` destructures them —
 * `attachments` is OMITTED when empty, so callers with nothing to attach
 * produce the byte-identical payload they always did.
 */
export function buildProjectHandoff({
  context = '',
  templateKey = null,
  unitSystemId,
  tutorial = false,
  autoMode = false,
  attachments = [],
} = {}) {
  const key = templateKey || null;
  const newProject = {
    context,
    templateKey: key,
    unitSystemId,
    // A tutorial walks a template's modules, and an unattended run needs a
    // tutorial — neither means anything on a blank canvas.
    tutorial: key ? !!tutorial : false,
    autoMode: key ? !!autoMode : false,
  };
  if (attachments && attachments.length) newProject.attachments = attachments;
  return { state: { isNew: true, newProject } };
}

/**
 * `createProject` + navigate, shared by the dashboard composer and `/learn`.
 *
 * `onCreated` exists because Dashboard owns the project list and wants its
 * optimistic insert; LearnPage has no list and passes nothing.
 */
export function useStartProject({ onCreated, onError } = {}) {
  const navigate = useNavigate();
  return useCallback(
    async (payload) => {
      try {
        const context = payload?.context || '';
        const project = await createProject(payload?.name, context);
        onCreated?.(project);
        // Deliberately NOT awaited: the LLM round trip would sit between the
        // click and the project opening, and its only effect is to replace the
        // locally-derived name with a better one on the next dashboard fetch.
        // `/learn` sends no description, so it never fires.
        if (context.trim()) summarizeProject(project.projectId).catch(() => {});
        navigate(`/project/${project.projectId}`, buildProjectHandoff(payload));
        return project;
      } catch (err) {
        onError?.(err.message || String(err));
        return null;
      }
    },
    [navigate, onCreated, onError]
  );
}
